import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { Environment, ENVIRONMENT } from '@hmax-order/frontend/environment';
import { StorageFileUrl, StorageUploadResult } from '@hmax-order/shared/types';

import { StorageFile } from '../../interfaces/storage-file.interface';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private _env = inject<Environment>(ENVIRONMENT);
  private _http = inject(HttpClient);

  private _apiUrl = `${this._env.apiUrl}/storage`;

  upload(file: File, subdirectory: string, fileName: string): Observable<StorageFile> {
    const formData = new FormData();
    formData.append('files', file, fileName);
    formData.append('subdirectory', subdirectory);

    return this._http.post<StorageUploadResult>(this._apiUrl, formData).pipe(
      map((res) => {
        const uploaded = res.success?.[0];
        if (!uploaded) throw new Error('Upload failed');
        return { url: uploaded.url, name: fileName };
      }),
    );
  }

  uploadMany(files: File[], subdirectory: string): Observable<StorageFile[]> {
    const formData = new FormData();
    formData.append('subdirectory', subdirectory);
    files.forEach((file) => formData.append('files', file, file.name));

    return this._http.post<StorageUploadResult>(this._apiUrl, formData).pipe(
      map((res) => {
        if (res.failed?.length) {
          throw new Error(`Failed to upload ${res.failed.length} file(s)`);
        }
        return (res.success ?? []).map((item) => ({
          url: item.url,
          name: files[item.index]?.name,
        }));
      }),
    );
  }

  listFiles(subdirectory: string): Observable<StorageFile[]> {
    const params = new HttpParams().set('subdirectory', subdirectory);
    return this._http
      .get<StorageFileUrl[]>(this._apiUrl, { params })
      .pipe(map((files) => files.map((f) => ({ url: f.url, name: f.url.split('/').pop() }))));
  }

  deleteFile(url: string): Observable<void> {
    const params = new HttpParams().set('url', url);
    return this._http.delete<void>(this._apiUrl, { params });
  }
}
